// src/oura/components/docs/threads/placeholderCleanUp.tsx
//
// "Clear old placeholders" (Docs phase 2, lane 4: 2.11), staff only, in the
// doc drawer's "Also in the doc" fold. The one-off clean-up of the comments
// that ask nothing ("Please leave any comments or suggestions"), across the
// client's docs. It counts first (a dry run), says the count in the foot and
// asks; only the second click deletes. Placeholders the client replied to
// stay.

import { useState, type ReactNode } from "react";
import { EraserIcon } from "lucide-react";
import { cleanUpPlaceholders, type PlaceholderCleanUp } from "@service/aeo/clientDocsWrite";
import { PillButton, type DrawerConfirm, type DrawerFootMessage } from "@oura/ui";
import { docsFailureText, plural } from "@oura/docs";
import { useSignedInName } from "@oura/docs/signedIn";

/** "Answer Plan (2) and GBP Pack Mont Kiara (1)": which docs, and how many in each. */
function docsWords(docs: PlaceholderCleanUp["documents"]): string {
    const parts = docs.map((d) => `${d.title} (${d.count})`);
    return parts.length <= 1 ? parts.join("") : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

export interface PlaceholderCleanUpAction {
    button: ReactNode | null;
    confirm: DrawerConfirm | null;
}

/** Call it on every render (it holds state); `enabled` false offers nothing. */
export function usePlaceholderCleanUp({
    businessId,
    businessName,
    enabled,
    onBusy,
    onNote,
}: {
    businessId: string;
    businessName: string;
    /** The write switch is on and the doc shows placeholders. */
    enabled: boolean;
    onBusy: (busy: boolean) => void;
    onNote: (m: DrawerFootMessage) => void;
}): PlaceholderCleanUpAction {
    const me = useSignedInName();
    const [busy, setBusy] = useState<"count" | "clear" | null>(null);
    // The dry run's answer, for this client only.
    const [found, setFound] = useState<{ businessId: string; run: PlaceholderCleanUp } | null>(null);
    if (!enabled) return { button: null, confirm: null };

    const begin = (what: "count" | "clear") => {
        setBusy(what);
        onBusy(true);
    };
    const end = () => {
        setBusy(null);
        onBusy(false);
    };

    const count = async () => {
        begin("count");
        const r = await cleanUpPlaceholders(businessId, { dryRun: true });
        end();
        if (!r.ok) return onNote({ tone: "error", text: docsFailureText(r, "the placeholders") });
        if (r.data.found === 0) return onNote({ tone: "info", text: `No old placeholders left in ${businessName}'s docs.` });
        setFound({ businessId, run: r.data });
    };
    const clear = async () => {
        begin("clear");
        const r = await cleanUpPlaceholders(businessId, { dryRun: false });
        end();
        setFound(null);
        if (!r.ok) return onNote({ tone: "error", text: docsFailureText(r, "the placeholders") });
        onNote({ tone: "success", text: `${me ?? "You"} cleared ${plural(r.data.removed, "old placeholder")} from ${docsWords(r.data.documents)}.` });
    };

    const pending = found && found.businessId === businessId ? found.run : null;
    return {
        button: (
            <PillButton variant="text" icon={EraserIcon} busy={busy === "count"} writes disabled={busy !== null} onClick={() => void count()}>
                Clear old placeholders
            </PillButton>
        ),
        confirm: pending
            ? {
                text: `${plural(pending.found, "old placeholder")} in ${plural(pending.documents.length, "doc")}: ${docsWords(pending.documents)}. They ask nothing. Delete them from ${businessName}'s docs?`,
                verb: `Clear ${pending.found}`,
                busy: busy === "clear",
                onConfirm: () => void clear(),
                onCancel: () => setFound(null),
            }
            : null,
    };
}
